import {
  useRef,
  type KeyboardEvent,
  type ReactElement,
  type RefCallback,
} from 'react';
import { Monitor, Moon, Sun, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useThemeContext } from '@/hooks/theme/useThemeContext';
import { usePreferencesQuery } from '@/hooks/preferences/usePreferencesQuery';
import { useTimestampFormat } from '@/hooks/useTimestampFormat';
import { THEME_PREFERENCES } from '@/styles/constants';
import type { ThemePreferenceType } from '@/styles/types';
import type { TimestampFormat } from '@/contexts/TimestampFormatContext';
import { cn } from '@/lib/utils';
import { Section } from './Section';
import { PreferencesSyncNotice } from './PreferencesSyncNotice';
import { ThemeSyncBadge } from './ThemeSyncBadge';

type ThemeOption = {
  value: ThemePreferenceType;
  label: string;
  icon: LucideIcon;
};

const THEME_OPTIONS: readonly ThemeOption[] = [
  { value: THEME_PREFERENCES.DARK, label: 'Dark', icon: Moon },
  { value: THEME_PREFERENCES.LIGHT, label: 'Light', icon: Sun },
  { value: THEME_PREFERENCES.SYSTEM, label: 'System', icon: Monitor },
] as const;

type TimestampFormatOption = {
  value: TimestampFormat;
  label: string;
};

const TIMESTAMP_FORMAT_OPTIONS: readonly TimestampFormatOption[] = [
  { value: 'relative', label: 'Relative' },
  { value: 'absolute', label: 'Absolute' },
] as const;

/**
 * Pure WAI-ARIA radiogroup navigation math shared by both inline radiogroups
 * (roving tabindex + arrow/Home/End). Returns the index to move to, or `null`
 * when the key is not a navigation key. Kept as a local pure helper (not a
 * shared component) so the sync-gated Theme control is untouched.
 */
function getNextRadioIndex(
  key: string,
  currentIdx: number,
  length: number
): number | null {
  if (key === 'ArrowRight' || key === 'ArrowDown') {
    return (currentIdx + 1) % length;
  }
  if (key === 'ArrowLeft' || key === 'ArrowUp') {
    return (currentIdx - 1 + length) % length;
  }
  if (key === 'Home') {
    return 0;
  }
  if (key === 'End') {
    return length - 1;
  }
  return null;
}

export function PreferencesSubTab(): ReactElement {
  const {
    value: activeTheme,
    onChange,
    isPending,
    hasSyncError,
    retrySync,
  } = useThemeContext();
  const { isError } = usePreferencesQuery();
  const { value: timestampFormat, setValue: setTimestampFormat } =
    useTimestampFormat();

  const themeOptionRefs = useRef<
    Partial<Record<ThemePreferenceType, HTMLButtonElement | null>>
  >({});
  const timestampOptionRefs = useRef<
    Partial<Record<TimestampFormat, HTMLButtonElement | null>>
  >({});

  const selectTheme = (next: ThemePreferenceType): void => {
    if (isPending) {
      return;
    }
    if (next !== activeTheme) {
      onChange(next);
    }
    themeOptionRefs.current[next]?.focus();
  };

  // WAI-ARIA radiogroup keyboard contract: roving tabindex + arrow nav +
  // Home/End. Mirrors the pattern already proven in SettingsTabBar so users
  // get the same predictable behaviour across the settings page.
  const handleThemeKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>
  ): void => {
    if (isPending) {
      return;
    }
    const currentIdx = THEME_OPTIONS.findIndex(o => o.value === activeTheme);
    if (currentIdx < 0) {
      return;
    }
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
      return;
    }
    const nextIdx = getNextRadioIndex(
      event.key,
      currentIdx,
      THEME_OPTIONS.length
    );
    if (nextIdx === null) {
      return;
    }
    event.preventDefault();
    selectTheme(THEME_OPTIONS[nextIdx].value);
  };

  const assignThemeRef =
    (value: ThemePreferenceType): RefCallback<HTMLButtonElement> =>
    el => {
      themeOptionRefs.current[value] = el;
    };

  const selectTimestampFormat = (next: TimestampFormat): void => {
    if (next !== timestampFormat) {
      setTimestampFormat(next);
    }
    timestampOptionRefs.current[next]?.focus();
  };

  const handleTimestampKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>
  ): void => {
    const currentIdx = TIMESTAMP_FORMAT_OPTIONS.findIndex(
      o => o.value === timestampFormat
    );
    if (currentIdx < 0) {
      return;
    }
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
      return;
    }
    const nextIdx = getNextRadioIndex(
      event.key,
      currentIdx,
      TIMESTAMP_FORMAT_OPTIONS.length
    );
    if (nextIdx === null) {
      return;
    }
    event.preventDefault();
    selectTimestampFormat(TIMESTAMP_FORMAT_OPTIONS[nextIdx].value);
  };

  const assignTimestampRef =
    (value: TimestampFormat): RefCallback<HTMLButtonElement> =>
    el => {
      timestampOptionRefs.current[value] = el;
    };

  return (
    <div className="space-y-7" data-testid="preferences-subtab">
      {isError && <PreferencesSyncNotice />}
      <Section
        title="Appearance"
        description="Customize the appearance of the Kirin interface"
      >
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Theme</CardTitle>
              <CardDescription>
                Set your preferred theme for the interface
              </CardDescription>
            </CardHeader>
            <CardContent className="px-6">
              <div
                role="radiogroup"
                aria-label="Theme preference"
                className={cn(
                  'flex flex-wrap gap-3',
                  isPending && 'opacity-60 pointer-events-none'
                )}
              >
                {THEME_OPTIONS.map(option => {
                  const Icon = option.icon;
                  const isActive = activeTheme === option.value;
                  return (
                    <Button
                      key={option.value}
                      ref={assignThemeRef(option.value)}
                      type="button"
                      role="radio"
                      aria-checked={isActive}
                      aria-disabled={isPending}
                      tabIndex={isActive ? 0 : -1}
                      variant="outline"
                      size="sm"
                      onClick={() => selectTheme(option.value)}
                      onKeyDown={handleThemeKeyDown}
                      className={cn(
                        'gap-2',
                        isActive &&
                          'bg-accent text-accent-foreground border-accent-border hover:bg-accent'
                      )}
                    >
                      <Icon className="h-4 w-4" aria-hidden="true" />
                      {option.label}
                    </Button>
                  );
                })}
              </div>
              {hasSyncError && (
                <ThemeSyncBadge onRetry={retrySync} isRetrying={isPending} />
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Timestamp Format</CardTitle>
              <CardDescription>
                Set your preferred format for timestamps
              </CardDescription>
            </CardHeader>
            <CardContent className="px-6">
              <div
                role="radiogroup"
                aria-label="Timestamp format"
                className="flex flex-wrap gap-3"
              >
                {TIMESTAMP_FORMAT_OPTIONS.map(option => {
                  const isActive = timestampFormat === option.value;
                  return (
                    <Button
                      key={option.value}
                      ref={assignTimestampRef(option.value)}
                      type="button"
                      role="radio"
                      aria-checked={isActive}
                      tabIndex={isActive ? 0 : -1}
                      variant="outline"
                      size="sm"
                      onClick={() => selectTimestampFormat(option.value)}
                      onKeyDown={handleTimestampKeyDown}
                      className={cn(
                        'gap-2',
                        isActive &&
                          'bg-accent text-accent-foreground border-accent-border hover:bg-accent'
                      )}
                    >
                      {option.label}
                    </Button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      </Section>
    </div>
  );
}
